import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Heart, MessageCircle, Users, Lock, ChevronRight } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import VideoDetailModal from "./VideoDetailModal";
import { sfxLike, sfxUnlike } from "@/lib/sfx";
import { triggerPointsAnimation } from "@/components/PointsRewardAnimation";
import { optimizeStorageUrl } from "@/lib/imageUtils";
import { useIsMobile } from "@/hooks/use-mobile";

type FriendPost = {
  id: string;
  user_id: string;
  media_url: string;
  media_type: string;
  thumbnail_url: string;
  artist: string;
  venue: string;
  description: string;
  created_at: string;
  like_count: number;
  comment_count: number;
  liked_by_me: boolean;
  display_name?: string;
  avatar_url?: string;
  carousel_urls?: string[];
};

const FriendsFeed = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [posts, setPosts] = useState<FriendPost[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [selectedPostId, setSelectedPostId] = useState<string | null>(null);

  const fetchFriendPosts = useCallback(async () => {
    if (!user) { setLoaded(true); return; }

    // Get friend IDs
    const { data: friendships } = await supabase
      .from("friends")
      .select("user_id, friend_id")
      .eq("status", "accepted")
      .or(`user_id.eq.${user.id},friend_id.eq.${user.id}`);

    if (!friendships || friendships.length === 0) { setLoaded(true); return; }

    const friendIds = friendships.map(f =>
      f.user_id === user.id ? f.friend_id : f.user_id
    );

    // Get posts from friends (public + friends visibility)
    const { data: postsData } = await (supabase as any)
      .from("posts")
      .select("*")
      .in("user_id", friendIds)
      .in("visibility", ["public", "friends"])
      .order("created_at", { ascending: false })
      .limit(12);

    if (!postsData || postsData.length === 0) { setLoaded(true); return; }

    const postIds = postsData.map((p: any) => p.id);
    const userIds = [...new Set(postsData.map((p: any) => p.user_id))] as string[];

    const [likesRes, commentsRes, profilesRes, myLikesRes] = await Promise.all([
      supabase.from("post_likes").select("post_id").in("post_id", postIds),
      supabase.from("post_comments").select("post_id").in("post_id", postIds),
      supabase.from("profiles").select("user_id, display_name, avatar_url").in("user_id", userIds),
      supabase.from("post_likes").select("post_id").eq("user_id", user.id).in("post_id", postIds),
    ]);

    const myLikes = new Set(myLikesRes.data?.map(l => l.post_id) ?? []);
    const likeCounts = new Map<string, number>();
    likesRes.data?.forEach(l => likeCounts.set(l.post_id, (likeCounts.get(l.post_id) || 0) + 1));
    const commentCounts = new Map<string, number>();
    commentsRes.data?.forEach(c => commentCounts.set(c.post_id, (commentCounts.get(c.post_id) || 0) + 1));
    const profileMap = new Map(profilesRes.data?.map(p => [p.user_id, p]) ?? []);

    const enriched: FriendPost[] = postsData.map((p: any) => ({
      ...p,
      carousel_urls: Array.isArray(p.carousel_urls) ? p.carousel_urls : [],
      like_count: likeCounts.get(p.id) || 0,
      comment_count: commentCounts.get(p.id) || 0,
      liked_by_me: myLikes.has(p.id),
      display_name: profileMap.get(p.user_id)?.display_name || undefined,
      avatar_url: profileMap.get(p.user_id)?.avatar_url || undefined,
    }));

    setPosts(enriched);
    setLoaded(true);
  }, [user]);

  useEffect(() => { fetchFriendPosts(); }, [fetchFriendPosts]);

  const handleLike = async (postId: string): Promise<boolean> => {
    if (!user) return false;
    await supabase.from("post_likes").insert({ post_id: postId, user_id: user.id });
    setPosts(prev => prev.map(p => p.id === postId ? { ...p, liked_by_me: true, like_count: p.like_count + 1 } : p));
    const { data: existing } = await supabase
      .from("points_ledger")
      .select("id")
      .eq("user_id", user.id)
      .eq("source_table", "post_likes")
      .eq("source_row_id", postId)
      .eq("event_type", "post_like_given")
      .limit(1);
    return !existing || existing.length === 0;
  };

  const handleUnlike = async (postId: string) => {
    if (!user) return;
    await supabase.from("post_likes").delete().eq("post_id", postId).eq("user_id", user.id);
    setPosts(prev => prev.map(p => p.id === postId ? { ...p, liked_by_me: false, like_count: Math.max(0, p.like_count - 1) } : p));
  };

  const selectedPost = posts.find(p => p.id === selectedPostId) || null;

  if (!user || (loaded && posts.length === 0)) return null;

  return (
    <section className="py-14 sm:py-20 relative">
      <div className="absolute top-0 left-1/3 w-[500px] h-[250px] bg-concert-cool/5 rounded-full blur-[120px] pointer-events-none" />

      <div className="container mx-auto px-4 relative">
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="mb-8 sm:mb-10 flex items-end justify-between"
        >
          <div>
            <div className="flex items-center gap-2 mb-2">
              <div className="h-1 w-8 rounded-full bg-concert-cool" />
              <span className="text-xs font-display font-bold text-concert-cool uppercase tracking-widest">Friends</span>
            </div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-foreground">From Your Friends</h2>
            <p className="mt-1.5 text-sm text-muted-foreground">Latest moments from people you follow</p>
          </div>
        </motion.div>

        {(() => {
          const COLS = 3;
          const MAX_VISIBLE = COLS * 2;
          const visiblePosts = posts.slice(0, MAX_VISIBLE - (posts.length > MAX_VISIBLE ? 1 : 0));
          const hasMore = posts.length > MAX_VISIBLE - 1;

          return (
            <div className={isMobile ? "flex flex-col items-center gap-3 px-4" : "grid grid-cols-3 gap-3 sm:gap-4 lg:gap-5"}>
              {visiblePosts.map((post, index) => (
                <motion.div
                  key={post.id}
                  initial={{ opacity: 0, y: 30, scale: 0.9 }}
                  whileInView={{ opacity: 1, y: 0, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.06, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                  className={`group relative overflow-hidden rounded-2xl border border-border/50 bg-card shadow-card hover:shadow-elevated hover:border-primary/20 transition-all duration-500 cursor-pointer ${isMobile ? "w-full max-w-[75vw]" : ""}`}
                  onClick={() => setSelectedPostId(post.id)}
                >
                  <div className="relative aspect-[3/4] overflow-hidden">
                    {post.media_type === "video" ? (
                      <video
                        src={post.media_url}
                        muted
                        loop
                        playsInline
                        preload="none"
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <img src={post.media_url} alt={post.artist} className="h-full w-full object-cover" />
                    )}
                    <div className="absolute inset-x-0 top-0 h-20 bg-gradient-to-b from-background/70 to-transparent pointer-events-none" />
                    <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-background/80 to-transparent pointer-events-none" />

                    <div className="absolute top-0 left-0 p-3 z-10 flex items-center gap-2">
                      <Avatar className="h-7 w-7 ring-2 ring-primary/40">
                        {post.avatar_url && <AvatarImage src={optimizeStorageUrl(post.avatar_url, 56)} />}
                        <AvatarFallback className="text-[9px] font-display bg-secondary text-foreground">
                          {(post.display_name || "?").slice(0, 2).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <span className="font-display font-bold text-foreground text-xs drop-shadow-md">
                        @{post.display_name || "fan"}
                      </span>
                    </div>
                  </div>

                  <div className="px-3 py-2.5">
                    <div className="flex items-center gap-3 mb-1">
                      <span className="flex items-center gap-1 text-xs text-muted-foreground">
                        <Heart className={`h-3.5 w-3.5 ${post.liked_by_me ? "fill-primary text-primary" : ""}`} />
                        {post.like_count}
                      </span>
                      <span className="flex items-center gap-1 text-xs text-muted-foreground">
                        <MessageCircle className="h-3.5 w-3.5" />
                        {post.comment_count}
                      </span>
                    </div>
                    {post.description && (
                      <p className="text-[11px] text-muted-foreground line-clamp-1">
                        <span className="font-semibold text-foreground mr-1">@{post.display_name || "fan"}</span>
                        {post.description}
                      </p>
                    )}
                  </div>
                </motion.div>
              ))}
              {hasMore && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  className="flex items-center justify-center rounded-2xl border border-border/50 bg-card/50 backdrop-blur-sm cursor-pointer group hover:border-primary/30 transition-colors min-h-[200px]"
                  onClick={() => {/* could navigate to a friends feed page */}}
                >
                  <div className="text-center px-4">
                    <div className="mx-auto mb-3 h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                      <ChevronRight className="h-6 w-6 text-primary" />
                    </div>
                    <p className="font-display font-bold text-sm text-foreground">See More</p>
                    <p className="text-xs text-muted-foreground mt-1">More from friends</p>
                  </div>
                </motion.div>
              )}
            </div>
          );
        })()}

        {selectedPost && (() => {
          const currentIndex = posts.findIndex(p => p.id === selectedPostId);
          const hasPrev = currentIndex > 0;
          const hasNext = currentIndex < posts.length - 1;
          return (
            <VideoDetailModal
              open={!!selectedPostId}
              onClose={() => setSelectedPostId(null)}
              post={{ id: selectedPost.id, artist: selectedPost.artist, media_url: selectedPost.media_url, media_type: selectedPost.media_type, description: selectedPost.description, venue: selectedPost.venue }}
              liked={selectedPost.liked_by_me}
              likeCount={selectedPost.like_count}
              commentCount={selectedPost.comment_count}
              displayName={selectedPost.display_name}
              avatarUrl={selectedPost.avatar_url}
              userId={selectedPost.user_id}
              onLike={() => handleLike(selectedPost.id)}
              onUnlike={() => handleUnlike(selectedPost.id)}
              onNext={hasNext ? () => setSelectedPostId(posts[currentIndex + 1].id) : undefined}
              onPrev={hasPrev ? () => setSelectedPostId(posts[currentIndex - 1].id) : undefined}
              hasNext={hasNext}
              hasPrev={hasPrev}
            />
          );
        })()}
      </div>
    </section>
  );
};

export default FriendsFeed;
